import { Store } from "@tanstack/react-store";
import type { ProductV3 } from "../types";
import { localStorage } from "../utils/local-storage";
import { getProducts, storeProduct } from "./all-products";

// Define an interface for the shopping item with order information
export interface ShoppingItem {
  code: string;
  quantity: number;
  displayOrder: number;
}

// Define the shopping products store structure
export interface ShoppingProductsState {
  items: ShoppingItem[];
}

// Initialize the store with empty state
export const shoppingProductsIndexStore = new Store<ShoppingProductsState>({
  items: [],
});

// Storage key for the shopping list index
export const SHOPPING_INDEX_KEY = "shopping_index";

let initialized = false;

// Load shopping products index from localStorage
export const loadShoppingProductsIndex = (): void => {
  const indexJson = localStorage?.getItem(SHOPPING_INDEX_KEY);
  if (indexJson && indexJson !== "undefined") {
    try {
      const parsedData = JSON.parse(indexJson);

      // Initialize new state structure
      const newState: ShoppingProductsState = {
        items: [],
      };

      if (Array.isArray(parsedData)) {
        // Convert from old Set format (array of codes) to new ordered format with default count of 1
        parsedData.forEach((code, index) => {
          const item: ShoppingItem = { code, quantity: 1, displayOrder: index };
          newState.items.push(item);
        });
        console.log(`Loaded ${parsedData.length} shopping items with default quantity 1`);
      } else if (typeof parsedData === "object") {
        if ("items" in parsedData && Array.isArray(parsedData.items)) {
          // Already in the new format with items array
          // Handle potential field name change from order to displayOrder
          newState.items = parsedData.items.map((item: any) => {
            if ("order" in item && !("displayOrder" in item)) {
              // Convert old 'order' field to new 'displayOrder'
              return {
                ...item,
                displayOrder: item.order,
              };
            }
            return item;
          });
        } else if ("itemMap" in parsedData && typeof parsedData.itemMap === "object") {
          // Handle case where old format with itemMap was persisted
          // Extract items from the itemMap and handle potential field name change
          const itemMap = parsedData.itemMap as Record<string, any>;
          newState.items = Object.values(itemMap).map((item: any) => {
            if ("order" in item && !("displayOrder" in item)) {
              // Convert old 'order' field to new 'displayOrder'
              return {
                ...item,
                displayOrder: item.order,
              };
            }
            return item;
          });
          console.log(
            `Migrated ${newState.items.length} shopping items from legacy itemMap format`
          );
        } else if (Object.values(parsedData).some((value) => typeof value !== "number")) {
          // Old object format but not with counters
          Object.keys(parsedData).forEach((code, index) => {
            const item: ShoppingItem = { code, quantity: 1, displayOrder: index };
            newState.items.push(item);
          });
        } else {
          // Old format with counters (Record<string, number>)
          Object.entries(parsedData).forEach(([code, quantity], index) => {
            const item: ShoppingItem = { code, quantity: quantity as number, displayOrder: index };
            newState.items.push(item);
          });
        }
        console.log(`Loaded ${newState.items.length} shopping items`);
      }

      // Sort items by displayOrder
      newState.items.sort((a, b) => a.displayOrder - b.displayOrder);

      shoppingProductsIndexStore.state = newState;
    } catch (e) {
      console.error("Failed to parse shopping index:", e);
      shoppingProductsIndexStore.state = { items: [] };
    }
  }

  initialized = true;
};

export const isInitialized = (): boolean => initialized;

// Subscribe to changes in the shopping index and save to localStorage
shoppingProductsIndexStore.subscribe(() => {
  console.log("shoppingProductsIndexStore updated");
  localStorage?.setItem(SHOPPING_INDEX_KEY, JSON.stringify(shoppingProductsIndexStore.state));
});

// Get all shopping products as a record for compatibility with existing code
export const getShoppingProducts = (): Record<string, ProductV3> => {
  return getProducts(shoppingProductsIndexStore.state.items.map((item) => item.code));
};

// Check if a product is in the shopping list
export const isShoppingProduct = (productCode: string): boolean => {
  return shoppingProductsIndexStore.state.items.some((item) => item.code === productCode);
};

// Get the quantity of a product in the shopping list
export const getShoppingProductQuantity = (productCode: string): number => {
  const item = shoppingProductsIndexStore.state.items.find((item) => item.code === productCode);
  return item?.quantity || 0;
};

// Add a product to shopping list and return the new quantity
export const addShoppingProduct = (product: ProductV3): number => {
  if (!product.code) return 0;

  // First store the product in the main product store
  const stored = storeProduct(product);
  if (!stored) return 0;

  let newQuantity = 1;

  // Update the shopping index, incrementing quantity if item exists
  shoppingProductsIndexStore.setState((prevState) => {
    const existingItemIndex = prevState.items.findIndex((item) => item.code === product.code);
    const existingItem = existingItemIndex !== -1 ? prevState.items[existingItemIndex] : null;

    if (existingItem) {
      // If product exists, increment the quantity
      newQuantity = existingItem.quantity + 1;
      const updatedItem = {
        ...existingItem,
        quantity: newQuantity,
      };

      const newItems = [...prevState.items];
      newItems[existingItemIndex] = updatedItem;

      return {
        items: newItems,
      };
    } else {
      // Create a new item with quantity 1
      const maxDisplayOrder =
        prevState.items.length > 0
          ? Math.max(...prevState.items.map((item) => item.displayOrder))
          : -1;

      const newItem: ShoppingItem = {
        code: product.code!,
        quantity: 1,
        displayOrder: maxDisplayOrder + 1,
      };

      return {
        items: [...prevState.items, newItem],
      };
    }
  });

  return newQuantity;
};

// Remove a product from shopping list
export const removeShoppingProduct = (productCode: string): boolean => {
  if (!productCode) return false;

  // Remove from the shopping index
  shoppingProductsIndexStore.setState((prevState) => {
    // If product doesn't exist, don't modify anything
    if (!prevState.items.some((item) => item.code === productCode)) {
      return prevState;
    }

    // Create a new items array without the removed product
    const newItems = prevState.items.filter((item) => item.code !== productCode);

    return {
      items: newItems,
    };
  });

  return true;
};

// Update the quantity of a product in the shopping list using a transform function
export const updateShoppingProductQuantity = (
  productCode: string,
  transform: (prevQuantity: number) => number
): boolean => {
  if (!productCode) return false;

  shoppingProductsIndexStore.setState((prevState) => {
    const itemIndex = prevState.items.findIndex((item) => item.code === productCode);

    // If product doesn't exist, don't modify anything
    if (itemIndex === -1) {
      return prevState;
    }

    const item = prevState.items[itemIndex];
    const currentQuantity = item.quantity;
    const newQuantity = transform(currentQuantity);

    // If quantity becomes negative, remove the product
    if (newQuantity < 0) {
      // Create a new items array without the removed product
      const newItems = prevState.items.filter((i) => i.code !== productCode);

      return {
        items: newItems,
      };
    }

    // Update the quantity
    const updatedItem = { ...item, quantity: newQuantity };

    // Create a new items array with the updated item
    const newItems = [...prevState.items];
    newItems[itemIndex] = updatedItem;

    return {
      items: newItems,
    };
  });

  return true;
};

// Clear all shopping products
export const clearShoppingProducts = (): void => {
  shoppingProductsIndexStore.setState(() => ({
    items: [],
  }));
};

// Load shopping products
export const loadShoppingProducts = (): void => {
  loadShoppingProductsIndex();
};

// Get ordered product codes
export const getOrderedProductCodes = (): string[] => {
  return shoppingProductsIndexStore.state.items
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((item) => item.code);
};

// Reorder a product in the shopping list
export const moveShoppingProduct = (productCode: string, direction: "up" | "down"): boolean => {
  if (!productCode) return false;

  shoppingProductsIndexStore.setState((prevState) => {
    // Find the item to move
    const items = [...prevState.items].sort((a, b) => a.displayOrder - b.displayOrder);
    const itemIndex = items.findIndex((item) => item.code === productCode);

    // If product doesn't exist or can't be moved in the requested direction, don't modify anything
    if (itemIndex === -1) return prevState;
    if (direction === "up" && itemIndex === 0) return prevState;
    if (direction === "down" && itemIndex === items.length - 1) return prevState;

    // Find the adjacent item to swap displayOrder with
    const adjacentIndex = direction === "up" ? itemIndex - 1 : itemIndex + 1;

    // Swap the displayOrder values
    const itemToMove = items[itemIndex];
    const adjacentItem = items[adjacentIndex];
    const tempDisplayOrder = itemToMove.displayOrder;

    // Create updated items with swapped displayOrder
    const updatedItemToMove = { ...itemToMove, displayOrder: adjacentItem.displayOrder };
    const updatedAdjacentItem = { ...adjacentItem, displayOrder: tempDisplayOrder };

    // Create a new items array with the updated items
    const newItems = items.map((item) => {
      if (item.code === productCode) return updatedItemToMove;
      if (item.code === adjacentItem.code) return updatedAdjacentItem;
      return item;
    });

    return {
      items: newItems,
    };
  });

  return true;
};
